import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, imgUrl, money } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Copy, Send, FileText, Upload } from "lucide-react";

const TABS = [
  { value: "", label: "All" },
  { value: "draft", label: "Drafts" },
  { value: "pending", label: "Pending Review" },
  { value: "active", label: "Live" },
  { value: "rejected", label: "Rejected" },
];

const APPROVAL_LABEL = {
  approved: { text: "Approved", cls: "text-green-700" },
  pending: { text: "Pending", cls: "text-[hsl(var(--kb-primary))]" },
  rejected: { text: "Rejected", cls: "text-red-700" },
};

export default function SellerProducts() {
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/products/seller/mine");
      // Defensive: backend returns a list. Normalize against unexpected shapes.
      setProducts(Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []));
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load products");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!tab) return products;
    return products.filter((p) => p.status === tab);
  }, [products, tab]);

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/seller/products/${id}/duplicate`);
      toast.success("Duplicated as draft");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveDraft = async (id) => {
    try { await api.put(`/seller/products/${id}/draft`); toast.success("Saved as draft"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitForApproval = async (id) => {
    try { await api.put(`/seller/products/${id}/submit`); toast.success("Submitted for approval"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/seller/products/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Created ${data.created} drafts. ${data.errors.length ? data.errors.length + " errors." : ""}`);
      if (data.errors.length) console.warn("CSV errors:", data.errors);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="seller-products-page">
      <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Catalogue</div>
          <h1 className="font-display text-4xl">Your Products</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={onCsv} data-testid="csv-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="kb-btn-outline flex items-center gap-2" data-testid="bulk-upload-btn">
            <Upload size={14} /> {uploading ? "Uploading…" : "Bulk Upload (CSV)"}
          </button>
          <Link to="/seller/products/new" className="kb-btn-primary flex items-center gap-2" data-testid="new-product-btn">
            <Plus size={16} /> New Product
          </Link>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.value || "all"}
            onClick={() => setTab(t.value)}
            className={`chip ${tab === t.value ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : ""}`}
            data-testid={`tab-${t.value || "all"}`}
          >
            {t.label} ({t.value ? products.filter((p) => p.status === t.value).length : products.length})
          </button>
        ))}
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]" data-testid="products-loading">Loading products…</div>
        ) : error ? (
          <div className="p-8 text-center text-red-700" data-testid="products-error">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]">No products in this tab.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--kb-surface-alt))] text-left text-[hsl(var(--kb-muted))]">
              <tr><th className="p-3">Image</th><th>Title</th><th>Price</th><th>Status</th><th>Approval</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const ap = APPROVAL_LABEL[p.approval] || { text: p.approval || "—", cls: "" };
                return (
                  <tr key={p.id} className="border-t border-[hsl(var(--kb-border))]" data-testid={`seller-prod-${p.slug}`}>
                    <td className="p-3 w-20">
                      {p.images?.[0] ? <img src={imgUrl(p.images[0])} className="w-14 aspect-[3/4] object-cover" alt="" /> : <div className="w-14 aspect-[3/4] bg-[hsl(var(--kb-surface-alt))]" />}
                    </td>
                    <td className="p-3 max-w-xs">
                      <div className="font-medium truncate">{p.title}</div>
                      <div className="text-xs text-[hsl(var(--kb-muted))] truncate">{p.color || "—"} · {p.fabric || "—"}</div>
                    </td>
                    <td className="p-3">{money(p.price)}</td>
                    <td className="p-3 uppercase text-xs">{p.status}</td>
                    <td className={`p-3 uppercase text-xs ${ap.cls}`}>{ap.text}</td>
                    <td className="p-3">
                      <div className="flex gap-2 flex-wrap">
                        <Link to={`/seller/products/${p.id}/edit`} className="kb-btn-outline !py-1 !px-2 !text-[10px]" data-testid={`edit-${p.slug}`}>
                          <Edit size={12} className="inline" /> Edit
                        </Link>
                        {p.status !== "draft" && (
                          <button onClick={() => saveDraft(p.id)} className="kb-btn-outline !py-1 !px-2 !text-[10px]" data-testid={`draft-${p.slug}`}>
                            <FileText size={12} className="inline" /> Draft
                          </button>
                        )}
                        {p.status !== "active" && p.approval !== "approved" && (
                          <button onClick={() => submitForApproval(p.id)} className="kb-btn-primary !py-1 !px-2 !text-[10px]" data-testid={`submit-${p.slug}`}>
                            <Send size={12} className="inline" /> Submit
                          </button>
                        )}
                        <button onClick={() => duplicate(p.id)} className="kb-btn-outline !py-1 !px-2 !text-[10px]" data-testid={`dup-${p.slug}`}>
                          <Copy size={12} className="inline" /> Duplicate
                        </button>
                        <button onClick={() => remove(p.id)} className="text-red-700 p-1" data-testid={`delete-${p.slug}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-4 text-xs text-[hsl(var(--kb-muted))]">
        CSV columns: <code>title,description,price,mrp,category_slug,color,fabric,occasion,sizes</code>. Sizes format: <code>S:10|M:5|L:3</code>.
      </p>
    </div>
  );
}
